import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { catalogService, CatalogType, CatalogItem } from '../../services/catalog.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

interface Tab {
  type: CatalogType;
  label: string;
  icon: string;
  hasKind: boolean;
  kindColumn?: string;
  fields: Array<{
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'select';
    options?: Array<{ value: string; label: string }>;
    required?: boolean;
  }>;
}

const CATALOG_TABS: Tab[] = [
  {
    type: 'concepts',
    label: 'Conceptos',
    icon: '💡',
    hasKind: true,
    kindColumn: 'entry_type',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'entry_type',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'movement_types',
    label: 'Tipos de Movimiento',
    icon: '🔀',
    hasKind: true,
    kindColumn: 'movement_kind',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'movement_kind',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'movement_details',
    label: 'Detalles de Movimiento',
    icon: '📋',
    hasKind: true,
    kindColumn: 'movement_kind',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'movement_kind',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'departments',
    label: 'Departamentos',
    icon: '🏢',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
    ],
  },
  {
    type: 'financial_locations',
    label: 'Ubicaciones Físicas',
    icon: '📍',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'location_type',
        label: 'Tipo de ubicación',
        type: 'select',
        required: true,
        options: [
          { value: 'drawer', label: 'Cajón' },
          { value: 'safe', label: 'Caja fuerte' },
          { value: 'cash_register', label: 'Caja registradora' },
          { value: 'locker', label: 'Casillero' },
          { value: 'other', label: 'Otro' },
        ],
      },
    ],
  },
  {
    type: 'currency_types',
    label: 'Tipos de Moneda',
    icon: '💵',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'code', label: 'Código (ej: cash, check)', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
    ],
  },
  {
    type: 'suppliers',
    label: 'Proveedores',
    icon: '🏭',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre / Razón social', type: 'text', required: true },
      { name: 'rfc', label: 'RFC', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'phone', label: 'Teléfono', type: 'text' },
      { name: 'contact_name', label: 'Persona de contacto', type: 'text' },
      { name: 'address', label: 'Dirección', type: 'textarea' },
      { name: 'category', label: 'Categoría', type: 'text' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ],
  },
  {
    type: 'contractors',
    label: 'Contratistas',
    icon: '🔨',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre completo', type: 'text', required: true },
      { name: 'rfc', label: 'RFC', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'phone', label: 'Teléfono', type: 'text' },
      { name: 'specialty', label: 'Especialidad', type: 'text' },
      { name: 'contact_name', label: 'Persona de contacto', type: 'text' },
      { name: 'address', label: 'Dirección', type: 'textarea' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ],
  },
];

export function CatalogsPage() {
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState<CatalogType>('concepts');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');

  const currentTab = CATALOG_TABS.find((t) => t.type === activeTab)!;

  useEffect(() => {
    loadItems();
  }, [activeTab]);

  const loadItems = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await catalogService.list(activeTab, { activeOnly: false });
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogo');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      item.name.toLowerCase().includes(term) ||
      (item.description && item.description.toLowerCase().includes(term))
    );
  });

  const handleOpenCreate = () => {
    setEditingItem(null);
    const initial: Record<string, any> = {};
    currentTab.fields.forEach((f) => {
      initial[f.name] = f.type === 'select' ? f.options?.[0]?.value || '' : '';
    });
    setFormData(initial);
    setShowModal(true);
  };

  const handleOpenEdit = (item: CatalogItem) => {
    setEditingItem(item);
    const initial: Record<string, any> = {};
    currentTab.fields.forEach((f) => {
      initial[f.name] = item[f.name] || '';
    });
    setFormData(initial);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
    setError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingItem) {
        await catalogService.update(activeTab, editingItem.id, formData);
        showSuccess('Registro actualizado');
      } else {
        await catalogService.create(activeTab, formData);
        showSuccess('Registro creado');
      }
      handleCloseModal();
      await loadItems();
    } catch (err: any) {
      showError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (item: CatalogItem) => {
    const action = item.is_active ? 'desactivar' : 'activar';
    if (!(await confirm({ message: `¿${action} "${item.name}"?`, danger: !!item.is_active }))) {
        return;
    }
    try {
      await catalogService.toggleActive(activeTab, item.id);
      showSuccess(`Registro ${action}do`);
      await loadItems();
    } catch (err: any) {
      showError(err.message || 'Error al cambiar estado');
    }
  };

  const getKindLabel = (item: CatalogItem) => {
    if (!currentTab.hasKind || !currentTab.kindColumn) return null;
    const value = item[currentTab.kindColumn];
    const labels: Record<string, { label: string; status: string }> = {
      I: { label: 'Ingreso', status: 'approved' },
      E: { label: 'Egreso', status: 'rejected' },
      B: { label: 'Ambos', status: 'draft' },
    };
    return labels[value] || null;
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Catálogos</h1>
          <p className="text-xs text-slate-500">{items.length} registros</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
        >
          + Nuevo
        </button>
      </div>

      <div className="relative">
        <input
          type="search"
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
        {CATALOG_TABS.map((tab) => (
          <button
            key={tab.type}
            onClick={() => setActiveTab(tab.type)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab.type
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {error && !showModal && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingState message={`Cargando ${currentTab.label.toLowerCase()}...`} />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          icon={currentTab.icon}
          title={items.length === 0 ? 'Sin registros' : 'Sin resultados'}
          description={
            items.length === 0
              ? `Aún no hay ${currentTab.label.toLowerCase()} registrados`
              : 'Prueba con otro término de búsqueda'
          }
        />
      ) : (
        <ul className="space-y-2">
          {filteredItems.map((item) => {
            const kindInfo = getKindLabel(item);
            return (
              <li key={item.id} className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {item.name}
                    </p>
                    {item.description && (
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                        {item.description}
                      </p>
                    )}
                    {activeTab === 'currency_types' && (
                      <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                        {item.code}
                      </p>
                    )}
                    {activeTab === 'financial_locations' && (
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Tipo: {item.location_type}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusBadge status={item.is_active ? 'active' : 'inactive'} />
                    {kindInfo && (
                      <StatusBadge status={kindInfo.status} label={kindInfo.label} />
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                  <button
                    onClick={() => handleOpenEdit(item)}
                    className="flex-1 py-1.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleToggleActive(item)}
                    className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${
                      item.is_active
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-emerald-50 text-emerald-700'
                    }`}
                  >
                    {item.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">
                {editingItem ? 'Editar' : 'Nuevo'} {currentTab.label.slice(0, -1)}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-slate-400 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              {currentTab.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {field.label}
                    {field.required && ' *'}
                  </label>
                  {field.type === 'text' && (
                    <input
                      type="text"
                      required={field.required}
                      value={formData[field.name] || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, [field.name]: e.target.value })
                      }
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                    />
                  )}
                  {field.type === 'textarea' && (
                    <textarea
                      rows={2}
                      required={field.required}
                      value={formData[field.name] || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, [field.name]: e.target.value })
                      }
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
                    />
                  )}
                  {field.type === 'select' && (
                    <select
                      required={field.required}
                      value={formData[field.name] || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, [field.name]: e.target.value })
                      }
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {isSaving ? 'Guardando...' : editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}